<template>
  <div class="md-layout" v-if="discount">
    <md-card>
      <md-card-header class="md-card-header-icon md-card-header-blue">
        <h4 class="title">Discount Details</h4>
      </md-card-header>

      <md-card-content>
        <ValidationObserver v-slot="{ handleSubmit }">
          <form @submit.prevent="handleSubmit(onSaveDiscount)">
            <div class="md-layout">
              <div class="md-layout-item md-size-100">
                <ValidationProvider
                  name="Discount Code"
                  rules="required"
                  v-slot="{ passed, failed }"
                >
                  <md-field
                    :class="[{ 'md-error': failed }, { 'md-valid': passed }]"
                  >
                    <label>Discount Code</label>
                    <md-input
                      :value="discount.code"
                      :disabled="isUsageLocked"
                      required
                      @input="
                        updateDiscountField({ field: 'code', value: $event })
                      "
                    />
                    <slide-y-down-transition>
                      <md-icon class="error" v-show="failed">close</md-icon>
                    </slide-y-down-transition>
                    <slide-y-down-transition>
                      <md-icon class="success" v-show="passed">done</md-icon>
                    </slide-y-down-transition>
                  </md-field>
                </ValidationProvider>
              </div>

              <div class="md-layout-item md-size-100">
                <ValidationProvider
                  name="Discount Code"
                  rules="required"
                  v-slot="{ passed, failed }"
                >
                  <md-field
                    :class="[{ 'md-error': failed }, { 'md-valid': passed }]"
                  >
                    <label>Description</label>
                    <md-input
                      :value="discount.description"
                      :disabled="isUsageLocked"
                      required
                      type="text"
                      @input="
                        updateDiscountField({
                          field: 'description',
                          value: $event
                        })
                      "
                    />
                    <slide-y-down-transition>
                      <md-icon class="error" v-show="failed">close</md-icon>
                    </slide-y-down-transition>
                    <slide-y-down-transition>
                      <md-icon class="success" v-show="passed">done</md-icon>
                    </slide-y-down-transition>
                  </md-field>
                </ValidationProvider>
              </div>

              <div class="md-layout-item md-size-50">
                <md-field>
                  <label>Discount Type</label>
                  <md-select
                    :value="discount.discount_type.value"
                    :disabled="isUsageLocked"
                    @input="onSelectValue('discount_type', $event)"
                  >
                    <md-option
                      v-for="type in discountTypes"
                      :key="type.value"
                      :value="type.value"
                    >
                      {{ type.key }}
                    </md-option>
                  </md-select>
                </md-field>
              </div>

              <div class="md-layout-item md-small-size-50 md-size-50">
                <md-datepicker
                  :value="discount.expiry_date"
                  md-immediately
                  :md-model-type="String"
                  @input="
                    updateDiscountField({ field: 'expiry_date', value: $event })
                  "
                >
                  <label>Expiry Date</label>
                </md-datepicker>
              </div>

              <div class="md-layout-item md-size-50">
                <ValidationProvider
                  name="Usage limit"
                  rules="numeric|min_value:0"
                  v-slot="{ passed, failed }"
                >
                  <md-field
                    class="number-field"
                    :class="[{ 'md-error': failed }, { 'md-valid': passed }]"
                  >
                    <label>Usage limit</label>
                    <md-input
                      :value="discount.usage_limit"
                      type="number"
                      placeholder="Unlimited"
                      @input="
                        updateDiscountField({
                          field: 'usage_limit',
                          value: $event === '' ? null : $event
                        })
                      "
                    />
                    <slide-y-down-transition>
                      <md-icon class="error" v-show="failed">close</md-icon>
                    </slide-y-down-transition>
                    <slide-y-down-transition>
                      <md-icon class="success" v-show="passed">done</md-icon>
                    </slide-y-down-transition>
                  </md-field>
                </ValidationProvider>
              </div>

              <div class="md-layout-item md-size-50">
                <md-field>
                  <label>Usage count</label>
                  <md-input :value="discount.usage_count" disabled />
                </md-field>
              </div>

              <div class="md-layout-item md-size-25">
                <md-switch v-model="discountStatus">
                  {{ getStatus() }}
                </md-switch>
              </div>

              <div class="md-layout-item md-size-25">
                <md-switch v-model="discountBehavior" :disabled="isUsageLocked">
                  {{ getDiscountBehavior() }}
                </md-switch>
              </div>

              <div class="md-layout-item md-size-50">
                <md-field>
                  <label>Applies to</label>
                  <md-select
                    :value="discount.applies_to.value"
                    :disabled="isUsageLocked"
                    @input="onSelectValue('applies_to', $event)"
                  >
                    <md-option
                      v-for="type in types"
                      :key="type.value"
                      :value="type.value"
                    >
                      {{ type.key }}
                    </md-option>
                  </md-select>
                </md-field>
              </div>

              <div
                class="md-layout-item md-size-100"
                v-if="
                  ['selected_adventures', 'restricted_to_adventures'].includes(
                    discount.applies_to.value
                  )
                "
              >
                <div
                  class="md-layout md-alignment-top-left tour-select tours-header"
                >
                  <h6>Selected Adventures</h6>
                  <div class="md-layout-item md-size-10 text-right icon-button">
                    <md-button
                      class="md-icon-button md-dense md-primary md-simple"
                      :disabled="isUsageLocked"
                      @click.prevent="openTourDialog"
                    >
                      <md-icon>add</md-icon>
                    </md-button>
                  </div>

                  <div class="md-layout-item md-size-100 tours">
                    <div v-if="!selectedTours.length" class="md-caption">
                      No adventures selected
                    </div>
                    <md-chips
                      v-else-if="!isUsageLocked"
                      v-model="selectedTourTitles"
                      class="md-primary"
                    >
                      <template slot="md-chip" slot-scope="{ chip }">
                        {{ chip }}
                      </template>
                    </md-chips>
                    <div v-else class="md-caption">
                      {{ selectedTourTitles.join(", ") }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="md-layout-item md-size-100 text-right">
                <md-button
                  class="md-raised md-success mt-2"
                  :disabled="savingDiscount"
                  type="submit"
                >
                  {{ discountButtonText }}
                </md-button>
              </div>
            </div>

            <div class="md-layout" id="options">
              <div class="md-layout-item md-size-90">
                <h6>Discount Rules</h6>
              </div>
              <div class="md-layout-item md-size-10 text-right">
                <md-button
                  class="md-icon-button md-dense md-primary md-round"
                  @click.prevent="addRule"
                  :disabled="!discount.id || isUsageLocked"
                >
                  <md-icon>add</md-icon>
                  <md-tooltip md-direction="top">
                    {{
                      discount.id
                        ? isUsageLocked
                          ? "Claimed discounts cannot be edited"
                          : "Add a Rule"
                        : "Save discount first"
                    }}
                  </md-tooltip>
                </md-button>
              </div>
            </div>

            <discount-rule
              v-for="(rule, index) in discount.rules"
              :key="rule.id ? `id-${rule.id}` : `tmp-${rule._tempId}`"
              :rule="rule"
              :index="index"
              :is-locked="isUsageLocked"
            />
          </form>
        </ValidationObserver>

        <enquiry-tour-dialog
          :viewTourDialog="viewTourDialog"
          :availableTours="availableTours"
          @add="addSelectedTour"
          @cancel="viewTourDialog = false"
        />
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import DiscountRule from "./DiscountRule.vue";
import { mapActions, mapGetters } from "vuex";
import { SlideYDownTransition } from "vue2-transitions";
import { extend } from "vee-validate";
import { required, numeric, min_value } from "vee-validate/dist/rules";
import tourApi from "@/services/api/tour";
import EnquiryTourDialog from "@/pages/Enquiry/Dashboard/EnquiryTourDialog.vue";
import { notifyError, notifySuccess } from "@/helpers/notify";
import { isDiscountUsed } from "@/helpers/discounts";

extend("required", required);
extend("numeric", numeric);
extend("min_value", min_value);

export default {
  components: { DiscountRule, EnquiryTourDialog, SlideYDownTransition },

  data() {
    return {
      savingDiscount: false,
      types: [
        { value: "all", key: "All" },
        { value: "tours", key: "Tours" },
        { value: "events", key: "Events" },
        { value: "selected_adventures", key: "Selected Adventures" }
      ],
      discountTypes: [
        { value: "discount", key: "Discount" },
        { value: "voucher", key: "Voucher" }
      ],
      tours: [],
      viewTourDialog: false
    };
  },

  computed: {
    ...mapGetters("discount", ["selectedDiscount", "isNewDiscount"]),
    discount() {
      return this.selectedDiscount;
    },
    discountButtonText() {
      return this.isNewDiscount ? "Create Discount" : "Update Discount";
    },
    discountStatus: {
      get() {
        return !!this.discount.active;
      },
      set(val) {
        this.updateDiscountField({ field: "active", value: !!val });
      }
    },
    discountBehavior: {
      get() {
        return !!this.discount.automatic;
      },
      set(value) {
        this.updateDiscountField({ field: "automatic", value: !!value });
      }
    },
    selectedTours: {
      get() {
        const selected = this.discount?.restricted_to_adventures || [];
        return selected
          .map(item => {
            if (item && typeof item === "object") return item;
            const tour = this.tours.find(tour => tour.id === item);
            return tour || { id: item, title: "Unknown Adventure" };
          })
          .filter(Boolean);
      },
      set(val) {
        this.updateDiscountField({
          field: "restricted_to_adventures",
          value: Array.isArray(val) ? val : []
        });
      }
    },
    availableTours() {
      return this.tours.filter(
        tour => !this.selectedTours.find(selected => selected.id === tour.id)
      );
    },
    selectedTourTitles: {
      get() {
        return this.selectedTours.map(tour => tour.title);
      },
      set(titles) {
        this.selectedTours = this.selectedTours.filter(tour =>
          titles.includes(tour.title)
        );
      }
    },
    isUsageLocked() {
      return isDiscountUsed(this.discount);
    }
  },

  methods: {
    ...mapActions("discount", [
      "addRule",
      "saveDiscount",
      "updateDiscountField"
    ]),

    onSelectValue(field, newValue) {
      const current = this.discount[field] || { value: "", key: "" };
      this.updateDiscountField({
        field,
        value: { ...current, value: newValue }
      });
    },

    getStatus() {
      return this.discount.active ? "Active" : "Inactive";
    },

    getDiscountBehavior() {
      return this.discount.automatic ? "Automatic" : "Manual";
    },

    async onSaveDiscount() {
      const isCreate = this.isNewDiscount;
      this.savingDiscount = true;
      try {
        await this.saveDiscount();
        notifySuccess(this, isCreate ? "Added!" : "Updated!");
      } catch (error) {
        notifyError(
          this,
          error?.response?.data?.message || "Unable to save the discount."
        );
      } finally {
        this.savingDiscount = false;
      }
    },

    async loadTours() {
      try {
        const resp = await tourApi.getTours();
        this.tours = resp.data?.tours || resp.data || [];
      } catch (error) {
        this.tours = [];
      }
    },

    openTourDialog() {
      this.viewTourDialog = true;
    },

    addSelectedTour(tourId) {
      const tour =
        this.tours.find(t => t.id === tourId) ||
        this.availableTours.find(t => t.id === tourId);

      if (!tour) {
        this.viewTourDialog = false;
        return;
      }

      this.selectedTours = [...this.selectedTours, tour];
      this.viewTourDialog = false;
    }
  },

  mounted() {
    this.loadTours();
  }
};
</script>

<style lang="scss" scoped>
#options {
  border-top: 1px solid #00bcd4;
  margin-top: 1em;
}

.tours-header {
  h6 {
    margin-bottom: 0;
  }
}

.tours {
  margin-bottom: 1rem;

  .md-chips {
    &.md-field {
      padding-top: 0;
      margin-top: 0px;
    }

    ::v-deep.md-chip {
      height: 30px;
      line-height: 30px;
    }
  }
}
.icon-button {
  padding-top: 5px;
}

.number-field {
  ::v-deep input[type="number"] {
    -moz-appearance: textfield;
  }

  ::v-deep input[type="number"]::-webkit-outer-spin-button,
  ::v-deep input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
